import { useCallback } from "react";
import { SnackbarType } from "../classes/enums/SnackbarTypeEnum";
import { useNotification } from "../classes/contexts/NotificationContext";
import { ImportStage } from "./useImageImport";

// Stage-specific messages and titles
const STAGE_CONFIG = {
  [ImportStage.UPLOADING]: {
    title: "Uploading files",
    message: "Uploading images to server",
  },
  [ImportStage.PROCESSING]: {
    title: "Processing images",
    message: "Generating thumbnails and study data",
  },
  [ImportStage.COMPLETE]: {
    title: "Import complete",
    message: "Files imported successfully",
  },
};

// Module-level storage for active import notification ID
// This persists even when components unmount
let activeImportNotificationId = null;

/**
 * Hook to manage import progress notifications.
 * Shows a persistent notification during import that updates with stage,
 * then transitions to success/error when complete.
 *
 * Uses module-level storage for the notification ID so it persists
 * even when the initiating component unmounts (e.g., popup closes).
 */
export const useImportNotification = () => {
  const { addNotification, updateNotification, removeNotification } =
    useNotification();

  const startImportNotification = useCallback(
    (message) => {
      const config = STAGE_CONFIG[ImportStage.UPLOADING];
      const id = addNotification(SnackbarType.LOADING, message || config.message, {
        duration: 0, // Persist until manually closed
        title: config.title,
      });
      activeImportNotificationId = id;
      return id;
    },
    [addNotification]
  );

  const updateImportStage = useCallback(
    (stage, customMessage) => {
      if (activeImportNotificationId) {
        const config = STAGE_CONFIG[stage] || STAGE_CONFIG[ImportStage.PROCESSING];
        updateNotification(activeImportNotificationId, {
          title: config.title,
          message: customMessage || config.message,
        });
      }
    },
    [updateNotification]
  );

  const updateImportNotification = useCallback(
    (message) => {
      if (activeImportNotificationId) {
        updateNotification(activeImportNotificationId, {
          message,
        });
      }
    },
    [updateNotification]
  );

  const completeImportNotification = useCallback(
    (success, message) => {
      if (activeImportNotificationId) {
        updateNotification(activeImportNotificationId, {
          type: success ? SnackbarType.SUCCESS : SnackbarType.ERROR,
          title: success ? "Import complete" : "Import failed",
          message: message || (success ? "Files imported successfully" : "An error occurred during import"),
          duration: 4000, // Auto-close after success/error
        });
        activeImportNotificationId = null;
      } else {
        console.warn("[useImportNotification] No notification ID to complete!");
      }
    },
    [updateNotification]
  );

  const cancelImportNotification = useCallback(() => {
    if (activeImportNotificationId) {
      removeNotification(activeImportNotificationId);
      activeImportNotificationId = null;
    }
  }, [removeNotification]);

  return {
    startImportNotification,
    updateImportStage,
    updateImportNotification,
    completeImportNotification,
    cancelImportNotification,
  };
};
